import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { SwPush } from '@angular/service-worker';
import { TranslocoModule } from '@jsverse/transloco';
import { NotificationPreferenceFlags, NotificationsService } from 'app';
import { I18nService } from 'i18n';
import { take } from 'rxjs/operators';
import { CommunityService } from '../../../services/community.service';
import { CommunityTranslationPreferenceService } from '../../../services/community-translation-preference.service';
import {
  COMMUNITY_TRANSLATION_LOCALES,
  CommunityTranslationMode,
  communitySameLocale
} from '../../../models/community.models';

type PreferenceField = 'in_app' | 'email' | 'push';

/**
 * `community/settings` — the notification preferences matrix (kind × in-app /
 * e-mail / push) plus the web-push opt-in. Mandatory kinds (moderation
 * notices, admin announcements, the news channel) are shown locked: the
 * backend ignores any attempt to turn them off anyway (§11).
 */
@Component({
  selector: 'app-community-settings-page',
  standalone: true,
  imports: [CommonModule, RouterLink, TranslocoModule],
  templateUrl: './community-settings-page.component.html',
  styleUrl: './community-settings-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommunitySettingsPageComponent implements OnInit {
  private notifications = inject(NotificationsService);
  private community = inject(CommunityService);
  private translationPrefs = inject(CommunityTranslationPreferenceService);
  private swPush = inject(SwPush, { optional: true });
  private i18n = inject(I18nService);
  private destroyRef = inject(DestroyRef);

  readonly fields: PreferenceField[] = ['in_app', 'email', 'push'];
  readonly translationModes: CommunityTranslationMode[] = ['ask', 'always', 'never'];

  loading = signal(true);
  error = signal<string | null>(null);
  saving = signal<string | null>(null);
  kinds = signal<string[]>([]);
  mandatory = signal<string[]>([]);
  preferences = signal<Record<string, NotificationPreferenceFlags>>({});
  pushEnabledOnServer = signal(false);

  // --- Translation (§3.4) ----------------------------------------------------------
  /** Whether a provider is configured; the whole section is hidden without one. */
  translationAvailable = signal(false);
  /** Every language a post can be written in, minus the one being read right now. */
  translationLocales = computed<string[]>(() =>
    COMMUNITY_TRANSLATION_LOCALES.filter(locale => !communitySameLocale(locale, this.lang))
  );
  translationSaving = this.translationPrefs.saving;
  translationError = this.translationPrefs.error;

  // --- Web push ------------------------------------------------------------------
  pushSupported = !!this.swPush?.isEnabled;
  pushPublicKey = signal<string | null>(null);
  pushSubscribed = signal(false);
  pushBusy = signal(false);
  pushError = signal<string | null>(null);

  ngOnInit(): void {
    this.load();
    this.loadPush();
    this.loadTranslation();
  }

  get lang(): string {
    return this.i18n.getLanguage();
  }

  communityLink(): string {
    return `/${this.lang}/community`;
  }

  isMandatory(kind: string): boolean {
    return this.mandatory().includes(kind);
  }

  flagsFor(kind: string): NotificationPreferenceFlags {
    return this.preferences()[kind] ?? { in_app: true, email: true, push: true };
  }

  load(): void {
    this.loading.set(true);
    this.notifications.getPreferences().subscribe({
      next: res => {
        this.loading.set(false);
        this.kinds.set(res.kinds);
        this.mandatory.set(res.mandatory);
        this.preferences.set(res.preferences);
        this.pushEnabledOnServer.set(res.push_enabled);
      },
      error: () => {
        this.loading.set(false);
        this.error.set('community.errors.generic');
      }
    });
  }

  toggle(kind: string, field: PreferenceField): void {
    if (this.isMandatory(kind)) return;
    const current = this.flagsFor(kind);
    const next = { ...current, [field]: !current[field] };
    this.preferences.update(p => ({ ...p, [kind]: next }));
    this.saving.set(kind);
    this.notifications.updatePreferences({ preferences: { [kind]: { [field]: next[field] } } }).subscribe({
      next: res => {
        this.saving.set(null);
        this.preferences.set(res.preferences);
      },
      error: () => {
        this.saving.set(null);
        // Roll back on failure.
        this.preferences.update(p => ({ ...p, [kind]: current }));
        this.error.set('community.errors.generic');
      }
    });
  }

  // --- Translation -----------------------------------------------------------------

  /**
   * The overview is the cheapest source of `translation_available`; the
   * preference matrix comes from its own endpoint because this page can be
   * opened cold, without ever having visited a thread.
   */
  private loadTranslation(): void {
    this.community.getOverview(this.lang).subscribe({
      next: res => {
        this.translationAvailable.set(res.translation_available === true);
        this.translationPrefs.hydrate(res.translation_preferences);
        if (res.translation_available === true) this.translationPrefs.load();
      },
      error: () => this.translationAvailable.set(false)
    });
  }

  languageName(locale: string): string {
    return this.i18n.getLanguageName(locale);
  }

  translationMode(locale: string): CommunityTranslationMode {
    return this.translationPrefs.modeFor(locale);
  }

  setTranslationMode(locale: string, mode: string): void {
    if (mode !== 'ask' && mode !== 'always' && mode !== 'never') return;
    this.translationPrefs.setMode(locale, mode);
  }

  // --- Web push --------------------------------------------------------------------

  private loadPush(): void {
    this.notifications.getPushPublicKey().subscribe({
      next: res => this.pushPublicKey.set(res.enabled ? res.public_key : null),
      error: () => this.pushPublicKey.set(null)
    });
    if (this.swPush) {
      this.swPush.subscription.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(sub => this.pushSubscribed.set(!!sub));
    }
  }

  subscribeToPush(): void {
    const key = this.pushPublicKey();
    if (!this.swPush || !key) return;
    this.pushError.set(null);
    this.pushBusy.set(true);
    this.swPush
      .requestSubscription({ serverPublicKey: key })
      .then(sub => {
        this.notifications.createPushSubscription(sub.toJSON()).subscribe({
          next: () => {
            this.pushBusy.set(false);
            this.pushSubscribed.set(true);
          },
          error: () => {
            this.pushBusy.set(false);
            this.pushError.set('community.errors.generic');
          }
        });
      })
      .catch(() => {
        this.pushBusy.set(false);
        this.pushError.set('community.settings.push_permission_denied');
      });
  }

  unsubscribeFromPush(): void {
    if (!this.swPush) return;
    this.pushBusy.set(true);
    this.pushError.set(null);
    this.swPush.subscription.pipe(take(1)).subscribe(sub => {
      const endpoint = sub?.endpoint;
      this.swPush!.unsubscribe()
        .then(() => {
          if (endpoint) {
            this.notifications.deletePushSubscription(endpoint).subscribe({ next: () => {}, error: () => {} });
          }
          this.pushBusy.set(false);
          this.pushSubscribed.set(false);
        })
        .catch(() => {
          this.pushBusy.set(false);
          this.pushError.set('community.errors.generic');
        });
    });
  }
}
